import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Calculator, Save, Loader2, DollarSign, FileText, ExternalLink } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import AdminBillReview from "@/components/AdminBillReview";

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
const genRowKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `row-${Math.random().toString(36).slice(2)}`);

/** view: "rate_card" = set-once pricing only · "monthly" = run-the-month only · "all" = both. */
export default function AdminBilling({ accounts = [], fixedAccountId = null, view = "all", initialMonth = null }) {
  const showRates = view === "all" || view === "rate_card";
  const showMonthly = view === "all" || view === "monthly";
  const linkable = useMemo(
    () => accounts.filter((a) => a.shipstation_store_id || a.shipstation_customer_id),
    [accounts]
  );
  const [accountId, setAccountId] = useState(fixedAccountId || "");
  useEffect(() => { if (fixedAccountId) setAccountId(fixedAccountId); }, [fixedAccountId]);
  const selectedAccount = useMemo(() => accounts.find((a) => a.id === accountId) || null, [accounts, accountId]);
  const [carriers, setCarriers] = useState([]);
  const [config, setConfig] = useState(null);
  const [markups, setMarkups] = useState({}); // code -> {markup_pct, markup_flat}
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(initialMonth || new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creditsKey, setCreditsKey] = useState(0);

  useEffect(() => {
    // Usage counts are per client, so reload when the selected store changes.
    // Guard against a slower all-accounts response landing after the per-client one.
    let stale = false;
    const q = accountId ? `?account_id=${accountId}` : "";
    api.get(`/admin/carriers${q}`).then(({ data }) => { if (!stale) setCarriers(data.carriers || []); }).catch(() => {});
    return () => { stale = true; };
  }, [accountId]);

  useEffect(() => {
    if (!accountId) { setConfig(null); setPreview(null); return; }
    api.get(`/admin/accounts/${accountId}/billing-config`).then(({ data }) => {
      const cfg = data.config;
      // Give each editable extra-charge row a stable key so add/remove doesn't smear inputs.
      cfg.extra_charges = (cfg.extra_charges || []).map((e) => ({ ...e, _key: e._key || genRowKey() }));
      cfg.packaging_charges = (cfg.packaging_charges || []).map((p) => ({ ...p, _key: p._key || genRowKey() }));
      setConfig(cfg);
      const m = {};
      (data.config.shipping_markups || []).forEach((x) => { m[String(x.carrier_code || "").toLowerCase()] = { markup_pct: x.markup_pct, markup_flat: x.markup_flat }; });
      setMarkups(m);
      setPreview(null);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [accountId]);

  const setF = (path, value) => {
    setConfig((c) => {
      const next = structuredClone(c);
      let o = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) o = o[parts[i]];
      o[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const setMarkup = (code, field, value) =>
    setMarkups((m) => ({ ...m, [code]: { ...(m[code] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      // Keep a carrier the moment either field has been filled in — including an
      // explicit 0, which means "no markup on this carrier" and must beat the default.
      const filled = (v) => v !== "" && v !== null && v !== undefined;
      const shipping_markups = Object.entries(markups)
        .filter(([, v]) => filled(v.markup_pct) || filled(v.markup_flat))
        .map(([carrier_code, v]) => ({
          carrier_code: String(carrier_code).toLowerCase(),
          markup_pct: num(v.markup_pct),
          markup_flat: num(v.markup_flat),
        }));
      const payload = {
        enabled: config.enabled,
        fulfillment: {
          tier_threshold: num(config.fulfillment.tier_threshold),
          tier1: { pick_fee: num(config.fulfillment.tier1.pick_fee), unit_rate: num(config.fulfillment.tier1.unit_rate) },
          tier2: { pick_fee: num(config.fulfillment.tier2.pick_fee), unit_rate: num(config.fulfillment.tier2.unit_rate) },
          max_fee: num(config.fulfillment.max_fee),
        },
        default_markup_pct: num(config.default_markup_pct),
        default_markup_flat: num(config.default_markup_flat),
        shipping_markups,
        prepayment: num(config.prepayment),
        prepayment_applies_to: config.prepayment_applies_to || "fulfillment",
        prepayment_rollover_pct: num(config.prepayment_rollover_pct),
        extra_charges: (config.extra_charges || []).filter((e) => e.label).map((e) => ({ label: e.label, amount: num(e.amount), per: e.per || "month" })),
        packaging_charges: (config.packaging_charges || []).filter((p) => (p.package_type || "").trim()).map((p) => ({ package_type: p.package_type.trim(), amount: num(p.amount) })),
      };
      await api.put(`/admin/accounts/${accountId}/billing-config`, payload);
      toast.success("Billing config saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const [y, m] = month.split("-");
      const { data } = await api.get(`/admin/accounts/${accountId}/billing?year=${y}&month=${m}`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const addExtra = () => setF("extra_charges", [...(config.extra_charges || []), { label: "", amount: 0, per: "month", _key: genRowKey() }]);
  const removeExtra = (i) => setF("extra_charges", config.extra_charges.filter((_, idx) => idx !== i));
  const addPackaging = () => setF("packaging_charges", [...(config.packaging_charges || []), { package_type: "", amount: 0, _key: genRowKey() }]);
  const removePackaging = (i) => setF("packaging_charges", config.packaging_charges.filter((_, idx) => idx !== i));

  return (
    <div data-testid="admin-billing" className="space-y-5">
      {!fixedAccountId && (
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <DollarSign className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-900 leading-relaxed">
          Configure shipping markups (per carrier), tiered fulfillment fees, prepayment and extra charges <strong>per store</strong>.
          These are admin-only — store owners only ever see the final marked-up totals on their dashboard.
        </p>
      </div>
      )}

      {!fixedAccountId && (
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Store / Account</label>
        <select
          data-testid="billing-account-select"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
        >
          <option value="">Select a store…</option>
          {linkable.map((a) => {
            const label = `${a.company_name} (${a.boxhero_account_id || a.id.slice(-6)})`;
            return <option key={a.id} value={a.id} label={label}>{label}</option>;
          })}
        </select>
      </div>
      )}

      {config && showRates && (
        <>
          {/* Fulfillment fee */}
          <Section title="Fulfillment fee" subtitle="Per order: MIN(pick fee + units × unit rate, max). Pick fee & unit rate switch at the order-count tier.">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Tier threshold (orders/mo)" value={config.fulfillment.tier_threshold} onChange={(v) => setF("fulfillment.tier_threshold", v)} testId="bill-tier-threshold" />
              <Field label="Max fee per order ($)" value={config.fulfillment.max_fee} onChange={(v) => setF("fulfillment.max_fee", v)} testId="bill-max-fee" />
              <div />
              <Field label={`Tier 1 pick fee ($) ≤ ${config.fulfillment.tier_threshold}`} value={config.fulfillment.tier1.pick_fee} onChange={(v) => setF("fulfillment.tier1.pick_fee", v)} testId="bill-t1-pick" />
              <Field label="Tier 1 unit rate ($)" value={config.fulfillment.tier1.unit_rate} onChange={(v) => setF("fulfillment.tier1.unit_rate", v)} testId="bill-t1-unit" />
              <div />
              <Field label={`Tier 2 pick fee ($) > ${config.fulfillment.tier_threshold}`} value={config.fulfillment.tier2.pick_fee} onChange={(v) => setF("fulfillment.tier2.pick_fee", v)} testId="bill-t2-pick" />
              <Field label="Tier 2 unit rate ($)" value={config.fulfillment.tier2.unit_rate} onChange={(v) => setF("fulfillment.tier2.unit_rate", v)} testId="bill-t2-unit" />
            </div>
          </Section>

          {/* Shipping markups */}
          <Section title="Shipping markups (per carrier)" subtitle="Final shipping fee = ShipStation cost × (1 + %) + flat. Leave a carrier blank to use the default below — type 0 to charge no markup at all on that carrier.">
            <div className="grid grid-cols-2 gap-4 mb-4 max-w-md">
              <Field label="Default markup %" value={config.default_markup_pct} onChange={(v) => setF("default_markup_pct", v)} testId="bill-default-pct" />
              <Field label="Default flat ($)" value={config.default_markup_flat} onChange={(v) => setF("default_markup_flat", v)} testId="bill-default-flat" />
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="py-2 px-3">Carrier</th>
                    <th className="py-2 px-3">Code</th>
                    <th className="py-2 px-3">Services used</th>
                    <th className="py-2 px-3 text-right">This client ships</th>
                    <th className="py-2 px-3 w-32">Markup %</th>
                    <th className="py-2 px-3 w-32">Flat ($)</th>
                    <th className="py-2 px-3">Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {carriers.map((c) => {
                    const mk = markups[c.code] || {};
                    const set = (mk.markup_pct !== "" && mk.markup_pct != null) || (mk.markup_flat !== "" && mk.markup_flat != null);
                    const used = (c.shipments_recent || 0) > 0;
                    return (
                      <tr key={c.code} data-testid={`markup-row-${c.code}`} className={`border-t border-slate-100 ${used ? "" : "bg-slate-50/50"}`}>
                        <td className={`py-2 px-3 font-medium ${used ? "text-slate-800" : "text-slate-400"}`}>{c.display_name}</td>
                        <td className="py-2 px-3"><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{c.code}</code></td>
                        <td className="py-2 px-3" data-testid={`markup-services-${c.code}`}>
                          {(c.services || []).length === 0 ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1 max-w-[280px]">
                              {c.services.slice(0, 4).map((s) => (
                                <span key={s.code || s.label} title={`${s.label} · ${s.count.toLocaleString()} shipments`}
                                  className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
                                  {s.label}<span className="text-slate-400 tabular-nums">{s.count.toLocaleString()}</span>
                                </span>
                              ))}
                              {c.services.length > 4 && (
                                <span className="text-[11px] text-slate-400">+{c.services.length - 4} more</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums" data-testid={`markup-usage-${c.code}`}>
                          {used ? <span className="font-semibold text-slate-700">{c.shipments_recent.toLocaleString()}</span> : <span className="text-slate-400">never used</span>}
                        </td>
                        <td className="py-2 px-3">
                          <input data-testid={`markup-pct-${c.code}`} type="number" step="0.1" value={mk.markup_pct ?? ""} placeholder="default"
                            onChange={(e) => setMarkup(c.code, "markup_pct", e.target.value)}
                            className="w-24 px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </td>
                        <td className="py-2 px-3">
                          <input data-testid={`markup-flat-${c.code}`} type="number" step="0.01" value={mk.markup_flat ?? ""} placeholder="default"
                            onChange={(e) => setMarkup(c.code, "markup_flat", e.target.value)}
                            className="w-24 px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </td>
                        <td className="py-2 px-3 text-xs" data-testid={`markup-applied-${c.code}`}>
                          {set
                            ? <span className="font-semibold text-slate-700">{num(mk.markup_pct)}% + ${num(mk.markup_flat).toFixed(2)}</span>
                            : <span className="text-slate-400">default {num(config.default_markup_pct)}% + ${num(config.default_markup_flat).toFixed(2)}</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {carriers.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">No carriers found.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              “This client ships” counts the shipments we have on file for this store, and “Services used” shows the shipping methods they actually buy from each carrier. Markups are set per carrier and cover every service under it. Save to apply — the bill matches on the carrier code shown here.
            </p>
          </Section>

          {/* Prepayment + extras */}
          <Section title="Prepayment & extra charges" subtitle="Prepayment is a monthly credit applied against the bill. Configure what it offsets and whether unused prepayment rolls over.">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <Field label="Monthly prepayment ($)" value={config.prepayment} onChange={(v) => setF("prepayment", v)} testId="bill-prepayment" />
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Applies toward</label>
                <select
                  data-testid="bill-prepayment-applies"
                  value={config.prepayment_applies_to || "fulfillment"}
                  onChange={(e) => setF("prepayment_applies_to", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                >
                  <option value="total">Total (shipping + fulfillment)</option>
                  <option value="shipping">Shipping only</option>
                  <option value="fulfillment">Fulfillment fees only</option>
                  <option value="none">Nothing (track only)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Rollover of unused (%)</label>
                <input
                  data-testid="bill-prepayment-rollover"
                  type="number" min="0" max="100" step="1"
                  value={config.prepayment_rollover_pct ?? 0}
                  onChange={(e) => setF("prepayment_rollover_pct", e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                />
                <p className="text-[11px] text-slate-400 mt-1">0 = no rollover · 100 = full leftover carries to next month</p>
              </div>
            </div>
            <div className="space-y-2">
              {(config.extra_charges || []).map((e, i) => (
                <div key={e._key || `extra-${i}`} className="flex items-center gap-2" data-testid={`extra-row-${i}`}>
                  <input value={e.label} onChange={(ev) => setF(`extra_charges.${i}.label`, ev.target.value)} placeholder="Label (e.g. Boxes)"
                    className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm" />
                  <input type="number" step="0.01" value={e.amount} onChange={(ev) => setF(`extra_charges.${i}.amount`, ev.target.value)} placeholder="0.00"
                    className="w-28 px-3 py-1.5 border border-slate-300 rounded text-sm" />
                  <select value={e.per} onChange={(ev) => setF(`extra_charges.${i}.per`, ev.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
                    <option value="month">per month</option>
                    <option value="order">per order</option>
                  </select>
                  <button onClick={() => removeExtra(i)} className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-extra-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={addExtra} data-testid="add-extra-btn" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-semibold mt-1">
                <Plus className="w-4 h-4" /> Add charge
              </button>
            </div>
          </Section>

          {/* Packaging charges */}
          <Section title="Packaging charges (per package type)" subtitle="Charged per shipment when the ShipStation package type matches. Type the code (e.g. small_poly) or its name (Small Poly) — both match. Run a preview below to see which types this client actually uses.">
            <div className="space-y-2">
              {(config.packaging_charges || []).map((p, i) => (
                <div key={p._key || `pkg-${i}`} className="flex items-center gap-2" data-testid={`packaging-row-${i}`}>
                  <input data-testid={`packaging-type-${i}`} value={p.package_type || ""} onChange={(ev) => setF(`packaging_charges.${i}.package_type`, ev.target.value)} placeholder="Package type (e.g. small_poly)"
                    className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm" />
                  <input data-testid={`packaging-amount-${i}`} type="number" step="0.01" value={p.amount ?? 0} onChange={(ev) => setF(`packaging_charges.${i}.amount`, ev.target.value)} placeholder="0.00"
                    className="w-28 px-3 py-1.5 border border-slate-300 rounded text-sm" />
                  <span className="text-xs text-slate-400">per shipment</span>
                  <button onClick={() => removePackaging(i)} className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-packaging-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={addPackaging} data-testid="add-packaging-btn" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-semibold mt-1">
                <Plus className="w-4 h-4" /> Add packaging charge
              </button>
            </div>
          </Section>

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="save-billing-config" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-md transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save config
            </button>
          </div>

          {/* Preview */}
        </>
      )}

      {config && showMonthly && (
        <>
          <Section title="Preview a billing month" subtitle="Run the calculation against live ShipStation data for any month (admin sees raw cost + markup).">
            <div className="flex items-end gap-3 mb-4">
              <div>
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Month</label>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="billing-preview-month" className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <button onClick={runPreview} disabled={previewing} data-testid="run-preview-btn" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-md transition-colors">
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Run preview
              </button>
            </div>
            {preview?.summary && <BillingPreview preview={preview} internal />}
          </Section>

          {preview?.summary && (
            <AdminBillReview
              accountId={accountId}
              month={month}
              preview={preview}
              onReload={() => { runPreview(); setCreditsKey((k) => k + 1); }}
            />
          )}

          <AdjustmentsPanel
            accountId={accountId}
            month={month}
            locked={!!preview?.lock?.locked}
            refreshKey={creditsKey}
            onChanged={() => { runPreview(); setCreditsKey((k) => k + 1); }}
          />

          <StripeInvoicePanel accountId={accountId} account={selectedAccount} month={month} />
        </>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <h3 className="text-base font-bold text-slate-900">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function StripeInvoicePanel({ accountId, account, month }) {
  const [invoices, setInvoices] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [y, m] = month.split("-");
  const hasStripe = !!account?.stripe_customer_id;

  const load = async () => {
    try { const { data } = await api.get(`/admin/accounts/${accountId}/stripe-invoices`); setInvoices(data.invoices || []); }
    catch (e) { /* noop */ }
  };
  useEffect(() => { if (accountId) load(); /* eslint-disable-next-line */ }, [accountId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/accounts/${accountId}/stripe-draft-invoice?year=${y}&month=${m}`);
      toast.success(data.already_existed ? "Draft invoice already exists — opening it" : "Stripe draft invoice created");
      window.open(data.dashboard_url, "_blank", "noopener");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setGenerating(false); }
  };

  return (
    <Section title="Stripe draft invoice" subtitle="Generate a DRAFT invoice in Stripe from the reviewed bill (shipping + fulfillment + packaging + extras − prepayment − credits). A locked month invoices the frozen snapshot. It is never auto-sent — review & finalize it in Stripe.">
      <div data-testid="stripe-invoice-panel">
        {!hasStripe && (
          <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2 mb-3">
            Link a Stripe Customer ID to this account (Accounts tab) before generating an invoice.
          </p>
        )}
        <button
          data-testid="generate-stripe-invoice-btn"
          onClick={generate}
          disabled={generating || !hasStripe}
          className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5147e6] disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-md transition-colors"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          Generate draft invoice for {month}
        </button>

        {invoices.length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="stripe-invoices-list">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="py-2 px-3">Period</th><th className="py-2 px-3">Status</th><th className="py-2 px-3 text-right">Amount</th><th className="py-2 px-3 text-right">Link</th></tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.stripe_invoice_id} data-testid={`stripe-invoice-${inv.stripe_invoice_id}`} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-medium text-slate-800">{inv.month_label}</td>
                    <td className="py-2 px-3"><span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{inv.status}</span></td>
                    <td className="py-2 px-3 text-right">{formatCurrency(inv.amount_due)}</td>
                    <td className="py-2 px-3 text-right">
                      <a href={inv.dashboard_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold">
                        Open <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Section>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input type="number" step="0.01" data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
    </div>
  );
}

export function BillingPreview({ preview, internal = false }) {
  const s = preview.summary;
  const [showAll, setShowAll] = useState(false);
  const ROW_CAP = 250;
  const allRows = preview.rows || [];
  const rows = showAll ? allRows : allRows.slice(0, ROW_CAP);
  const contracted = s.prepayment ?? s.prepayment_base ?? 0;
  const applied = s.prepayment_applied ?? 0;
  return (
    <div data-testid="billing-preview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Orders" value={s.order_count} />
        <Stat label="Units" value={s.units_total} />
        <Stat label="Shipping" value={formatCurrency(s.shipping_total)} />
        <Stat label="Fulfillment" value={formatCurrency(s.fulfillment_total)} />
        {s.packaging_total > 0 && <Stat label="Packaging" value={formatCurrency(s.packaging_total)} />}
        <Stat label="Grand total" value={formatCurrency(s.grand_total)} />
        {contracted > 0 && <Stat label="Prepayment" value={formatCurrency(contracted)} testId="billing-prepayment-stat" />}
        {internal && contracted > 0 && applied !== contracted && (
          <Stat label="Prepayment applied" value={`− ${formatCurrency(applied)}`} testId="billing-prepayment-applied-stat" />
        )}
        {s.credits_total > 0 && <Stat label="Credits" value={`− ${formatCurrency(s.credits_total)}`} />}
        <Stat label="To be billed" value={formatCurrency(s.to_be_billed)} accent />
        <Stat label="Avg / order" value={formatCurrency(s.avg_per_order_total)} />
      </div>

      {(s.credit_lines || []).length > 0 && (
        <div className="mb-4 border border-emerald-200 bg-emerald-50/50 rounded-lg p-3" data-testid="billing-credit-lines">
          <p className="text-xs uppercase tracking-wider font-semibold text-emerald-800 mb-1.5">Credits applied to this bill</p>
          <ul className="space-y-1">
            {s.credit_lines.map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm text-emerald-900">
                <span>
                  {c.note}
                  {c.order_number ? <span className="text-emerald-700/70"> · order {c.order_number}</span> : null}
                  <span className="ml-2 text-[11px] uppercase tracking-wide text-emerald-700/70">{c.source}</span>
                </span>
                <span className="font-semibold">− {formatCurrency(c.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-96 overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="py-2 px-3">Order</th><th className="py-2 px-3">Date</th>
              {internal && <th className="py-2 px-3">Brand</th>}
              <th className="py-2 px-3">Carrier</th>
              <th className="py-2 px-3 text-right">Units</th>
              {internal && <th className="py-2 px-3 text-right">Raw</th>}
              {internal && <th className="py-2 px-3 text-right">Markup</th>}
              <th className="py-2 px-3 text-right">Shipping</th><th className="py-2 px-3 text-right">Fulfillment</th>
              {s.packaging_total > 0 && <th className="py-2 px-3 text-right">Packaging</th>}
              <th className="py-2 px-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.order_number}-${i}`} className="border-t border-slate-100">
                <td className="py-1.5 px-3 font-medium">
                  {r.order_number}
                  {r.duplicate && <span data-testid={`row-dup-${r.order_number}`} className="ml-1.5 text-[10px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">DUP</span>}
                </td>
                <td className="py-1.5 px-3 text-slate-500">{(r.ship_date || "").slice(0, 10)}</td>
                {internal && <td className="py-1.5 px-3 text-slate-500 max-w-[180px] truncate" title={r.brand}>{r.brand || "—"}</td>}
                <td className="py-1.5 px-3">{r.carrier}</td>
                <td className="py-1.5 px-3 text-right">{r.units}</td>
                {internal && <td className="py-1.5 px-3 text-right text-slate-400">{r.raw_cost != null ? formatCurrency(r.raw_cost) : "—"}</td>}
                {internal && <td className="py-1.5 px-3 text-right text-slate-400">{r.markup_pct}%{r.markup_flat ? ` +${formatCurrency(r.markup_flat)}` : ""}</td>}
                <td className="py-1.5 px-3 text-right">{formatCurrency(r.shipping_fee)}</td>
                <td className="py-1.5 px-3 text-right">{formatCurrency(r.fulfillment_fee)}</td>
                {s.packaging_total > 0 && <td className="py-1.5 px-3 text-right">{formatCurrency(r.packaging_fee || 0)}</td>}
                <td className="py-1.5 px-3 text-right font-semibold">{formatCurrency(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {allRows.length > ROW_CAP && (
        <div className="text-center mt-2">
          <button
            data-testid="billing-rows-toggle"
            onClick={() => setShowAll((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
          >
            {showAll ? `Show first ${ROW_CAP} rows` : `Show all ${allRows.length} rows`}
          </button>
          {!showAll && <p className="text-xs text-slate-400 mt-0.5">Showing {ROW_CAP} of {allRows.length} shipments · totals above always cover the full month</p>}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`rounded-lg p-3 border ${accent ? "bg-orange-50 border-orange-200" : "bg-slate-50 border-slate-200"}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-lg font-bold ${accent ? "text-orange-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

/* =====================================================================
   ADJUSTMENTS — one place for a charge (+) or a credit (−) on this month
   ===================================================================== */
function AdjustmentsPanel({ accountId, month, locked, refreshKey = 0, onChanged }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: "charge", label: "", detail: "", mode: "flat", amount: "", qty: "", order_number: "" });
  const [saving, setSaving] = useState(false);
  const [y, m] = month.split("-");

  const load = async () => {
    try {
      const [ch, cr] = await Promise.all([
        api.get(`/admin/accounts/${accountId}/charges?year=${y}&month=${m}`),
        api.get(`/admin/accounts/${accountId}/credits?year=${y}&month=${m}`),
      ]);
      const charges = (ch.data.charges || []).map((c) => ({
        id: c.id, kind: "charge", label: c.label, detail: c.detail, order_number: c.order_number,
        mode: c.mode, amount: c.amount, qty: c.qty, by: c.created_by,
      }));
      const credits = (cr.data.credits || []).map((c) => ({
        id: c.id, kind: "credit", label: c.note, detail: null, order_number: c.order_number,
        mode: "flat", amount: c.amount, source: c.source, by: c.created_by,
      }));
      setItems([...charges, ...credits]);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { if (accountId) load(); /* eslint-disable-next-line */ }, [accountId, month, refreshKey]);

  const add = async () => {
    if (!form.label.trim() || !(Number(form.amount) > 0)) { toast.error("Add a description and an amount"); return; }
    setSaving(true);
    try {
      if (form.kind === "credit") {
        await api.post(`/admin/accounts/${accountId}/credits`, {
          note: form.label.trim() + (form.detail.trim() ? ` — ${form.detail.trim()}` : ""),
          order_number: form.order_number.trim() || null,
          amount: Number(form.amount),
          year: Number(y), month: Number(m),
        });
      } else {
        await api.post(`/admin/accounts/${accountId}/charges?year=${y}&month=${m}`, {
          label: form.label.trim(),
          detail: form.detail.trim() || null,
          mode: form.mode,
          amount: Number(form.amount),
          qty: form.mode === "qty" ? Number(form.qty || 0) : null,
          order_number: form.order_number.trim() || null,
        });
      }
      toast.success(form.kind === "credit" ? "Credit applied to this month" : "Charge added to this month");
      setForm({ kind: form.kind, label: "", detail: "", mode: "flat", amount: "", qty: "", order_number: "" });
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (it) => {
    try {
      await api.delete(it.kind === "credit" ? `/admin/credits/${it.id}` : `/admin/charges/${it.id}`);
      toast.success("Adjustment removed");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const basis = (it) => {
    if (it.kind === "credit") return it.source && it.source !== "manual" ? it.source : "credit";
    if (it.mode === "per_order") return "× every shipment";
    if (it.mode === "qty") return `× ${it.qty}`;
    return "one-off";
  };

  return (
    <Section title="Adjustments" subtitle={`A charge or a credit against the ${month} bill. The description and detail appear on the customer's statement.`}>
      {!locked && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-4" data-testid="adjustment-form">
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Type</label>
            <select data-testid="adjustment-kind" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="charge">Charge (+)</option>
              <option value="credit">Credit (−)</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Description</label>
            <input data-testid="adjustment-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder={form.kind === "credit" ? "Goodwill credit" : "Pallet rework"} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Detail the customer sees</label>
            <input data-testid="adjustment-detail" value={form.detail} onChange={(e) => setForm((f) => ({ ...f, detail: e.target.value }))} placeholder="Repacked 3 pallets damaged in transit" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
          {form.kind === "charge" && (
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">How it's charged</label>
              <select data-testid="adjustment-mode" value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
                <option value="flat">One-off amount</option>
                <option value="per_order">Per shipment</option>
                <option value="qty">Amount × quantity</option>
              </select>
            </div>
          )}
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">$</label>
            <input data-testid="adjustment-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
          {form.kind === "charge" && form.mode === "qty" && (
            <div className="md:col-span-1">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Qty</label>
              <input data-testid="adjustment-qty" type="number" step="1" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
          )}
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Order # (optional)</label>
            <input data-testid="adjustment-order" value={form.order_number} onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
          <div className="md:col-span-2">
            <button onClick={add} disabled={saving} data-testid="add-adjustment-btn" className="w-full inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-md transition-colors">
              <Plus className="w-4 h-4" /> Add adjustment
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-slate-400">No adjustments on this month.</p>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="adjustments-list">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="py-2 px-3">Type</th><th className="py-2 px-3">Description</th><th className="py-2 px-3">Detail (customer sees)</th><th className="py-2 px-3">Basis</th><th className="py-2 px-3 text-right">Amount</th><th className="py-2 px-3"></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={`${it.kind}-${it.id}`} data-testid={`adjustment-row-${it.id}`} className="border-t border-slate-100">
                  <td className="py-2 px-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${it.kind === "credit" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {it.kind === "credit" ? "Credit −" : "Charge +"}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-medium text-slate-800">
                    {it.label}
                    {it.order_number && <span className="text-slate-400"> · order {it.order_number}</span>}
                  </td>
                  <td className="py-2 px-3 text-slate-600">{it.detail || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2 px-3 text-slate-500">{basis(it)}</td>
                  <td className={`py-2 px-3 text-right font-semibold ${it.kind === "credit" ? "text-emerald-700" : "text-slate-900"}`}>
                    {it.kind === "credit" ? "− " : "+ "}{formatCurrency(it.amount)}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {!locked && (
                      <button onClick={() => remove(it)} data-testid={`remove-adjustment-${it.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
